import Role from './Model/Role';
import { BasicUserWithPermissions } from './Model/User';
import ResourceTyped from 'Resources/ResourceTyped';
import { TYPE, STYLE } from 'Store/reducers/SystemNotificationReducer';

export type Dashboard = {
	id: number;
	isFavorite: boolean;
	roles: Optional<Role, 'userEditable' | 'userRemovable'>[];
	users: BasicUserWithPermissions[];
	useNext: boolean;
	space: string;
	title: string;
	roleIds?: number[];
	// The individuals granted this dashboard, alongside the roles. Administrators only - the
	// API leaves both id lists out for everyone else.
	userIds?: number[];
};
class LookerDashboard extends ResourceTyped<Dashboard> {
	notifications: { [key: string]: () => { [key: string]: string } };

	constructor() {
		super('looker/dashboards');

		/*
			The copy the Angular factory produced, spelled out. lookerDashboard.js ran every
			favorite() response through ResourceHelper.onSave(..., isNew=false) with
			updateSuccessBody: 'updated.lookerDashboard' and updateErrorBody:
			'saveError.lookerDashboard'.

			Keyed on favorite/favoriteError, not save/saveError: _handleNotification looks up
			this.notifications[opts.methodName] (and methodName + 'Error'), so these cannot fire
			for the inherited save() that Components/Admin/Looker/Looker.tsx uses — that call
			site owns its own toast and must not gain a second one. Same shape as
			MailCampaign.ts's archive/archiveError beside its save pair.

			The Angular helper debounced the toast by 500 ms; that is not ported. A favourite
			click is one deliberate action, and one toast per star is not worth the machinery.

			ResourceHelper.onUpdate also broadcast lookerDashboard.updated and sent an
			'Updated lookerDashboard' Segment event. The broadcast has no listeners anywhere in
			the repo, so no eventName is set here — and the analytics half rides on the same
			_triggerEvent call, so it goes with it. Currency.ts took the same call for
			currency.updated; if the event turns out to be wanted, both come back together.

			The copy is admittedly wrong for this action — "Dashboard access updated" is about
			role/user grants, not favourites — and ui-3's setDashboardFavorite shows no toast at
			all. Changing or removing it is a product decision, not part of this conversion.
		*/
		this.notifications = {
			favorite: () => ({
				title: 'default.updated',
				body: 'updated.lookerDashboard',
				style: STYLE.SUCCESS,
				icon: 'check',
				type: TYPE.BODY
			}),
			favoriteError: () => ({
				title: 'default.error',
				body: 'saveError.lookerDashboard',
				style: STYLE.ERROR,
				icon: 'times',
				type: TYPE.BODY
			})
		};
	}

	/**
	 * Favourites are per user, so this is a POST to a sub-path rather than a field on the
	 * dashboard. `_getUrl` appends the segment, giving
	 * `/api/v2/looker/dashboards/<id>/favorite`, which the route's optional trailing slash
	 * (`/looker/dashboards/:id/favorite/?$`) accepts.
	 */
	favorite(id: Dashboard['id'], isFavorite: boolean) {
		return this._postRequest(`${id}/favorite`, { isFavorite }, { methodName: 'favorite' });
	}
}

const LookerDashboardResource = new LookerDashboard();

export default LookerDashboardResource;
